<template>
  <div class="stat-courier-page">
    <v-container fluid>
      <stat-top-filter
        :filial-default="globalFilialId"
        hide-days-diff
        @applyFilter="fetchData"
      >
        <template #date-picker>
          <period-date-picker
            :period-type.sync="periodType"
            @selected="selectDate"
          />
        </template>
        <template #additional-filter>
          <label class="stat-filter__label">
            выбрать <br />
            коннекторы:
          </label>
          <connector-select v-model="connectorIds" />
        </template>
      </stat-top-filter>

      <dynamics-indicators-list
        v-if="Object.keys(indicators).length"
        class="mb-5"
        :indicators="indicators"
        :loading="isLoading"
      />

      <chart-container
        class="mb-5"
        :loading="isLoading"
        :no-data="!revenueDynamics.length"
      >
        <template #chart>
          <dynamics-chart
            v-if="revenueDynamics.length"
            title="Выручка"
            :items="revenueDynamics"
            :period-type="chartPeriodType"
            legend-container-id="connector-revenue-dynamics"
          />
        </template>
      </chart-container>

      <chart-container
        class="mb-5"
        :loading="isLoading"
        :no-data="!ordersDynamics.length"
      >
        <template #chart>
          <dynamics-chart
            v-if="ordersDynamics.length"
            title="Кол-во заказов"
            :items="ordersDynamics"
            :period-type="chartPeriodType"
            legend-container-id="connector-orders-dynamics"
          />
        </template>
      </chart-container>

      <v-row>
        <v-col md="6" cols="12">
          <chart-container
            style="height: 100%"
            :loading="isLoading"
            :no-data="proportionEmpty"
          >
            <template #chart>
              <proportion-chart
                title="Доля в выручке"
                :items="proportion"
                :colors="proportionColors"
                legend-container-id="connector-proportion"
              />
            </template>
          </chart-container>
        </v-col>
        <v-col md="6" cols="12">
          <chart-container
            style="height: 100%"
            :loading="isLoading"
            :no-data="proportionByDeliveryEmpty"
          >
            <template #chart>
              <proportion-chart
                title="Типы заказов"
                :items="proportionByDelivery"
                :colors="proportionByDeliveryColors"
                legend-container-id="connector-proportion-by-delivery"
              />
            </template>
          </chart-container>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import StatTopFilter from '@/components/stat/StatTopFilter.vue'
  import PeriodDatePicker from '@/components/ui/PeriodDatePicker.vue'
  import ConnectorSelect from '@/components/stat/connectors/ConnectorSelect.vue'
  import ChartContainer from '@/components/stat/ChartContainer.vue'
  import ProportionChart from '@/components/stat/revenue/ProportionChart.vue'
  import DynamicsChart from '@/components/stat/connectors/revenue/DynamicsChart.vue'
  import DynamicsIndicatorsList from '@/components/stat/connectors/revenue/DynamicsIndicatorsList.vue'
  import PeriodType from '@/models/enums/statistics/PeriodType'
  import chartColors from '@/constants/chartColors'
  import revenueDynamicsApi from '@/api/statistics/connectors/revenue/dynamics'
  import revenueProportionApi from '@/api/statistics/connectors/revenue/proportion'
  import ordersDynamicsApi from '@/api/statistics/connectors/orders-dynamics'

  export default {
    name: 'ConnectorsRevenueDynamics',
    components: {
      StatTopFilter,
      PeriodDatePicker,
      ConnectorSelect,
      ChartContainer,
      ProportionChart,
      DynamicsChart,
      DynamicsIndicatorsList,
    },
    data () {
      return {
        isLoading: false,
        indicators: {},
        revenueDynamics: [],
        ordersDynamics: [],
        proportion: [],
        proportionByDelivery: [],
        connectorIds: [],
        date: [],
        periodType: PeriodType.Week,
        chartPeriodType: PeriodType.Week,
      }
    },
    computed: {
      ...mapGetters({
        globalFilialId: 'branches/getGlobalFilialId',
      }),
      proportionColors () {
        return this.proportion.map(
          (item, index) => chartColors[index % chartColors.length],
        )
      },
      proportionByDeliveryColors () {
        return this.proportionByDelivery.map(
          (item, index) => chartColors[index % chartColors.length],
        )
      },
      proportionEmpty () {
        return (
          !this.proportion.length ||
          this.proportion.every((item) => !item.percent)
        )
      },
      proportionByDeliveryEmpty () {
        return this.proportionByDelivery.every((item) => !item.percent)
      },
    },
    methods: {
      selectDate (date) {
        this.date = date
      },
      fetchData (filter) {
        this.$nextTick(() => {
          this.chartPeriodType = this.periodType

          const data = {
            filialPreset: filter.filialPreset,
            periodType: this.periodType,
            connectorIds: this.connectorIds,
          }

          if (filter.filialId) {
            data.filialIds = Array.isArray(filter.filialId)
              ? filter.filialId
              : [filter.filialId]
          }

          switch (this.periodType) {
            case PeriodType.Week:
              data.dateRange = this.date
              break
            case PeriodType.Month:
              data.month = this.date
              break
            case PeriodType.Year:
              data.year = this.date
          }

          this.isLoading = true

          Promise.all([
            revenueDynamicsApi.fetchRevenueDynamicsIndicators(data),
            revenueDynamicsApi.fetchRevenueDynamics(data),
            revenueProportionApi.fetchRevenueProportion(data),
            revenueProportionApi.fetchRevenueProportionByDelivery(data),
            ordersDynamicsApi.fetchOrdersDynamics(data),
          ])
            .then((responses) => {
              this.indicators = responses[0].data
              this.revenueDynamics = responses[1].items
              this.proportion = this.prepareProportionData(responses[2].items)
              this.proportionByDelivery = this.prepareProportionData(
                responses[3].items,
              )
              this.ordersDynamics = responses[4].items
            })
            .catch((err) => this.showErrorFromResponse(err))
            .finally(() => {
              this.isLoading = false
            })
        })
      },
      prepareProportionData (items) {
        return items.map((item) => {
          if (item.value) {
            item.formattedValue = this.$options.filters.amountWithCurrency0(
              item.value,
            )
          }

          return item
        })
      },
    },
  }
</script>

<style lang="less" scoped>
@import '@/assets/styles/statistic.less';
</style>
